<script setup>
import { computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useInbox } from 'dashboard/composables/useInbox';
import { toast } from 'dashboard/helper/toast';
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

// Channel::Studio é o inbox de simulação do playground. As "conversas
// anteriores" são simulações passadas do mesmo contato de teste. O card normal
// navega pra fora do Studio (router.push); no playground a gente intercepta o
// clique e CARREGA a conversa como chat ativo (mesmo caminho do startSession),
// pra revisar/continuar uma simulação sem sair da tela.
const { isAStudioChannel } = useInbox();

const conversationGetter = useMapGetter(
  'contactConversations/getContactConversation'
);
const uiFlags = useMapGetter('contactConversations/getUIFlags');

const conversations = computed(() => conversationGetter.value(props.contactId));

const previousConversations = computed(() =>
  conversations.value.filter(
    conversation => conversation.id !== Number(props.conversationId)
  )
);

// No playground (Studio) o clique no card NÃO navega: carrega a conversa como
// chat ativo. Reusa o caminho de produção do playground — GET conversations/:id
// (popula allConversations com messages) + setActiveChat — pra hidratar a thread
// por completo, já que o card da lista pode trazer só a última mensagem.
const onStudioConversationSelect = async conversation => {
  if (conversation.id === Number(props.conversationId)) return;
  // Falha em voz alta: 404/rede no carregamento não pode sumir em silêncio (o
  // clique não faria nada). Avisa o usuário em vez de engolir o erro.
  try {
    await store.dispatch('getConversation', conversation.id);
    const loaded = store.getters.getConversationById(conversation.id);
    if (!loaded) {
      toast.error(t('AI.PLAYGROUND.LOAD_CONVERSATION_ERROR'));
      return;
    }
    await store.dispatch('setActiveChat', { data: loaded });
  } catch (error) {
    toast.error(t('AI.PLAYGROUND.LOAD_CONVERSATION_ERROR'));
  }
};

watch(
  () => props.contactId,
  (newContactId, prevContactId) => {
    if (newContactId && newContactId !== prevContactId) {
      store.dispatch('contactConversations/get', newContactId);
    }
  }
);

onMounted(() => {
  store.dispatch('contactConversations/get', props.contactId);
});
</script>

<template>
  <div v-if="!uiFlags.isFetching" class="">
    <div v-if="!previousConversations.length" class="no-label-message px-4 p-3">
      <span>
        {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
      </span>
    </div>
    <div v-else class="contact-conversation--list">
      <!-- Studio: intercepta o clique em captura/stop ANTES do handler interno do
           card (que navegaria pra fora do playground) e carrega a conversa como
           chat ativo. Sem context menu (open-new-tab/copy-link também sairiam). -->
      <template v-if="isAStudioChannel">
        <div
          v-for="conversation in previousConversations"
          :key="conversation.id"
          @click.capture.stop="onStudioConversationSelect(conversation)"
        >
          <ConversationCard
            :chat="conversation"
            :hide-inbox-name="false"
            hide-thumbnail
            compact
          />
        </div>
      </template>
      <template v-else>
        <ConversationCard
          v-for="conversation in previousConversations"
          :key="conversation.id"
          :chat="conversation"
          :hide-inbox-name="false"
          hide-thumbnail
          enable-context-menu
          compact
          :allowed-context-menu-options="['open-new-tab', 'copy-link']"
        />
      </template>
    </div>
  </div>
  <div v-else class="flex items-center justify-center py-5">
    <Spinner />
  </div>
</template>

<style lang="scss" scoped>
.no-label-message {
  @apply text-n-slate-11 mb-4;
}
</style>
